// Debug yaw/pitch readout — ships in the build but is hidden by default.
// Useful for placing hotspots over real panorama features: drag-aim at the
// feature, read the values, paste into rooms.json.
//
// An operator tool, so it is reached through the menu's `debug` entry, which
// only exists under ?operator=1 (or ?edit=1 / ?embed=1). That entry also owns
// the `D` shortcut — hence no keydown listener here, since two listeners on one
// key would toggle twice and cancel out.

import type { CameraControls } from '../playcanvas/cameraControls';

export class DebugLook {
  private readonly root: HTMLElement;
  private readonly yawEl: HTMLElement;
  private readonly pitchEl: HTMLElement;
  private readonly controls: CameraControls;
  private rafHandle = 0;

  constructor(controls: CameraControls) {
    this.controls = controls;
    this.root = mustGet('debug-look');
    this.yawEl = mustGet('debug-yaw');
    this.pitchEl = mustGet('debug-pitch');
  }

  show(): void {
    this.root.classList.remove('hidden');
    if (!this.rafHandle) this.tick();
  }

  hide(): void {
    this.root.classList.add('hidden');
    if (this.rafHandle) {
      cancelAnimationFrame(this.rafHandle);
      this.rafHandle = 0;
    }
  }

  toggle(): void {
    if (this.root.classList.contains('hidden')) this.show();
    else this.hide();
  }

  private tick = (): void => {
    const { yaw, pitch } = this.controls.getLook();
    // Normalize yaw to [-180, 180] for easier reading.
    let y = yaw % 360;
    if (y > 180) y -= 360;
    if (y <= -180) y += 360;
    this.yawEl.textContent = `yaw   ${pad(y)}°`;
    this.pitchEl.textContent = `pitch ${pad(pitch)}°`;
    this.rafHandle = requestAnimationFrame(this.tick);
  };
}

function mustGet(id: string): HTMLElement {
  const el = document.getElementById(id);
  if (!el) throw new Error(`[debugLook] missing #${id}`);
  return el;
}

function pad(n: number): string {
  const sign = n >= 0 ? '+' : '−';
  const abs = Math.abs(n).toFixed(1).padStart(5, ' ');
  return `${sign}${abs}`;
}
